import { Component, input, output, inject, effect, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  SimplifiedAutomationEditorComponent,
  SimplifiedAutomationEditorEvent,
  SimplifiedAutomationRunStateChange,
  SimplifiedWorkflowInput,
} from './simplified-automation-editor.component';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { AutomataService, Automation } from '@vendasta/automata';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { toSignal, toObservable } from '@angular/core/rxjs-interop';
import { switchMap, of, catchError, firstValueFrom, combineLatest, BehaviorSubject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'automata-simplified-automation-editor-container',
  standalone: true,
  imports: [CommonModule, SimplifiedAutomationEditorComponent, GalaxyLoadingSpinnerModule],
  templateUrl: './simplified-automation-editor-container.component.html',
  styleUrl: './simplified-automation-editor-container.component.scss',
})
export class SimplifiedAutomationEditorContainerComponent {
  private automataService = inject(AutomataService);
  private snackbarService = inject(SnackbarService);
  private translateService = inject(TranslateService);

  // Signal-based inputs
  automationId = input<string>('');
  disableTriggerEdit = input<boolean>(false);

  // Signal-based outputs
  dataChange = output<SimplifiedWorkflowInput>();
  automationRunStateChange = output<SimplifiedAutomationRunStateChange>();
  errorChange = output<string | null>();
  editAutomationDetails = output<SimplifiedAutomationEditorEvent>();

  // Loading and error states
  isLoading = signal(false);
  error = signal<string | null>(null);

  // Animation states for fade effect
  isAnimating = signal(false);
  showEditor = signal(false);

  // Reload trigger for refreshing automation data
  private reloadTrigger = signal(0);

  // Track loading start time for minimum shimmer duration
  private loadingStartTime: number | null = null;
  private dataLoaded = signal(false);
  private minTimeElapsed = signal(false);

  // Convert signal input to observable for reactive streams
  private automationId$ = toObservable(this.automationId);
  private reloadTrigger$ = toObservable(this.reloadTrigger);
  reloadAdvancedMode$$ = new BehaviorSubject<boolean>(false);

  // Reactive automation fetching
  automation = toSignal(
    combineLatest([this.automationId$, this.reloadTrigger$, this.reloadAdvancedMode$$]).pipe(
      switchMap(([id, _trigger, _reloadAdvancedMode]) => {
        if (!id) {
          return of(null);
        }
        this.startLoadingAnimation();
        this.error.set(null);
        this.errorChange.emit(null);
        return this.automataService.getAutomation(id).pipe(
          catchError((err) => {
            console.error('Error fetching automation:', err);
            const unknownError = this.translateService.instant('AUTOMATIONS.EDITOR.SIMPLIFIED_EDITOR.UNKNOWN_ERROR');
            const errorMessage = `${this.translateService.instant('AUTOMATIONS.EDITOR.SIMPLIFIED_EDITOR.FAILED_TO_LOAD_AUTOMATION')}: ${err.message || unknownError}`;
            this.error.set(errorMessage);
            this.errorChange.emit(errorMessage);
            this.endLoadingAnimation();
            return of(null);
          }),
        );
      }),
    ),
  );

  // Extract step data from automation using the API
  workflowData = toSignal(
    combineLatest([this.automationId$, this.reloadTrigger$, this.reloadAdvancedMode$$]).pipe(
      switchMap(([id, _trigger, _reloadAdvancedMode]) => {
        if (!id?.trim()) {
          return of(null);
        }
        return this.automataService.evaluateStepDataForAutomation(id).pipe(
          catchError((err) => {
            console.error('Error fetching step data:', err);
            this.endLoadingAnimation();
            // Return empty step data on error
            return of({ stepData: [], unableToLoadData: true });
          }),
        );
      }),
    ),
  );

  constructor() {
    // Mark data as loaded when automation and step data are available
    effect(() => {
      const automation = this.automation();
      const stepData = this.workflowData();
      if (automation !== undefined && stepData !== undefined && this.isLoading()) {
        this.dataLoaded.set(true);
      }
    });

    // End loading animation only when both minimum time has elapsed AND data is loaded
    effect(() => {
      if (this.dataLoaded() && this.minTimeElapsed() && this.isLoading()) {
        this.endLoadingAnimation();
      }
    });

    // Show editor when data is available and not animating
    effect(() => {
      const automation = this.automation();
      const stepData = this.workflowData();
      if (automation && stepData && !this.isLoading()) {
        this.showEditor.set(true);
      }
    });
  }

  /**
   * Handles data changes from the simplified automation editor.
   * This is called when the user clicks "Apply Changes" in the editor.
   */
  async onDataChange(updatedData: SimplifiedWorkflowInput): Promise<void> {
    try {
      await firstValueFrom(this.automataService.updateStepDataForAutomation(this.automationId(), updatedData.stepData));
      this.snackbarService.openSuccessSnack(
        this.translateService.instant('AUTOMATIONS.EDITOR.SIMPLIFIED_EDITOR.STEP_DATA_UPDATED_SUCCESSFULLY'),
      );
      // Trigger reload of automation and step data
      this.reloadTrigger.set(this.reloadTrigger() + 1);
      // Emit the updated data to parent
      this.dataChange.emit(updatedData);
    } catch (error) {
      console.error('Error updating step data:', error);
      this.snackbarService.openErrorSnack(
        this.translateService.instant('AUTOMATIONS.EDITOR.SIMPLIFIED_EDITOR.ERROR_UPDATING_STEP_DATA'),
      );
    }
  }

  onAutomationRunStateChange(event: { automation: Automation; turnOn: boolean }): void {
    this.automationRunStateChange.emit(event);
  }

  onIsAdvancedModeChange(event: boolean): void {
    this.reloadAdvancedMode$$.next(event);
  }

  /**
   * Starts the loading animation by fading out the editor
   */
  private startLoadingAnimation(): void {
    this.loadingStartTime = Date.now();
    this.isLoading.set(true);
    this.isAnimating.set(true);
    this.showEditor.set(false);
    this.dataLoaded.set(false);
    this.minTimeElapsed.set(false);

    // Set minimum time elapsed after 700ms
    setTimeout(() => {
      this.minTimeElapsed.set(true);
    }, 700);
  }

  /**
   * Ends the loading animation and shows the editor
   */
  private endLoadingAnimation(): void {
    this.isLoading.set(false);
    this.showEditor.set(true);
    // Small delay to allow fade-in to start before removing animation state
    setTimeout(() => {
      this.isAnimating.set(false);
    }, 300);
  }
}
